import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Skeleton,
  Link,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { BarChart } from "@mui/x-charts/BarChart";
import { motion } from "framer-motion";
import { PieChart } from "@mui/x-charts/PieChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PublicIcon from "@mui/icons-material/Public";
import LanguageIcon from "@mui/icons-material/Language";
import PlaceIcon from "@mui/icons-material/Place";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightlightIcon from "@mui/icons-material/Nightlight";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import api from "../../config/axios";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// Fallback mock data when API is empty or fails
const MOCK_VISITORS = {
  today: 1247,
  thisWeek: 8923,
  thisMonth: 34210,
  thisYear: 287450,
  overall: 512890,
};

const MOCK_TOP_PAGES = [
  { page: "Home", path: "/", visits: 125430 },
  { page: "Products", path: "/products", visits: 89200 },
  { page: "Track Order", path: "/track-order", visits: 45670 },
  { page: "Cart", path: "/cart", visits: 32100 },
  { page: "Checkout", path: "/checkout", visits: 18900 },
  { page: "About", path: "/about", visits: 12400 },
  { page: "Contact", path: "/contact", visits: 8900 },
];

const MOCK_LOCATIONS = [
  { country: "India", city: "Mumbai", visits: 98500, flag: "🇮🇳" },
  { country: "India", city: "Delhi", visits: 76200, flag: "🇮🇳" },
  { country: "India", city: "Bangalore", visits: 54300, flag: "🇮🇳" },
  { country: "India", city: "Hyderabad", visits: 32100, flag: "🇮🇳" },
  { country: "United States", city: "New York", visits: 18500, flag: "🇺🇸" },
  { country: "United Arab Emirates", city: "Dubai", visits: 12200, flag: "🇦🇪" },
  { country: "United Kingdom", city: "London", visits: 9800, flag: "🇬🇧" },
];

const MOCK_DEVICES = [
  { device: "Mobile", visits: 312400, percent: 61 },
  { device: "Desktop", visits: 178200, percent: 35 },
  { device: "Tablet", visits: 22290, percent: 4 },
];

const MOCK_DAILY_VISITS = [
  { day: "Mon", visits: 18500 },
  { day: "Tue", visits: 22100 },
  { day: "Wed", visits: 19800 },
  { day: "Thu", visits: 24500 },
  { day: "Fri", visits: 27200 },
  { day: "Sat", visits: 31200 },
  { day: "Sun", visits: 28900 },
];

// Visit time analytics – by time range (most active periods)
const MOCK_VISITS_BY_TIME_RANGE = [
  { label: "Night", range: "12am – 6am", visits: 18200, percent: 6, icon: "night" },
  { label: "Morning", range: "6am – 12pm", visits: 128400, percent: 42, icon: "morning" },
  { label: "Afternoon", range: "12pm – 6pm", visits: 102500, percent: 33, icon: "afternoon" },
  { label: "Evening", range: "6pm – 12am", visits: 56790, percent: 19, icon: "evening" },
];

// Last 24h rolling – mock labels (24h ago … current hour)
const MOCK_VISITS_BY_HOUR = Array.from({ length: 24 }, (_, i) => ({
  hour: i === 0 ? "24h ago" : i === 23 ? "Now" : `${24 - i}h ago`,
  visits: Math.round(8000 + 4000 * Math.sin((i / 24) * Math.PI * 2) + Math.random() * 2000),
}));

const MOCK_RECENT_ACTIVITIES = [
  { id: 1, visitor_id: "vis_8f3a91", path: "/products", page_name: "Products Catalog", visited_at: new Date(Date.now() - 1000 * 60 * 12).toISOString(), city: "Mumbai", country: "India", flag: "🇮🇳", device: "Mobile" },
  { id: 2, visitor_id: "vis_1c4b72", path: "/orders/104", page_name: "Order #104 Details", visited_at: new Date(Date.now() - 1000 * 60 * 35).toISOString(), city: "Delhi", country: "India", flag: "🇮🇳", device: "Desktop" },
  { id: 3, visitor_id: "vis_9e2d14", path: "/cart", page_name: "Shopping Cart", visited_at: new Date(Date.now() - 1000 * 60 * 58).toISOString(), city: "Bangalore", country: "India", flag: "🇮🇳", device: "Mobile" },
  { id: 4, visitor_id: "vis_7a6d88", path: "/add-address/88", page_name: "Add Delivery Address", visited_at: new Date(Date.now() - 1000 * 60 * 120).toISOString(), city: "Dubai", country: "United Arab Emirates", flag: "🇦🇪", device: "Mobile" },
  { id: 5, visitor_id: "vis_3f8e55", path: "/", page_name: "Home Page", visited_at: new Date(Date.now() - 1000 * 60 * 185).toISOString(), city: "London", country: "United Kingdom", flag: "🇬🇧", device: "Desktop" },
  { id: 6, visitor_id: "vis_4b9c10", path: "/track-order", page_name: "Shipment Tracking", visited_at: new Date(Date.now() - 1000 * 60 * 240).toISOString(), city: "New York", country: "United States", flag: "🇺🇸", device: "Mobile" },
  { id: 7, visitor_id: "vis_2d1e67", path: "/finance", page_name: "Financial Overview", visited_at: new Date(Date.now() - 1000 * 60 * 310).toISOString(), city: "Hyderabad", country: "India", flag: "🇮🇳", device: "Desktop" },
];

const visitorCards = [
  { key: "today", label: "Today", icon: TodayIcon },
  { key: "thisWeek", label: "This Week", icon: DateRangeIcon },
  { key: "thisMonth", label: "This Month", icon: CalendarMonthIcon },
  { key: "thisYear", label: "This Year", icon: TrendingUpIcon },
  { key: "overall", label: "Overall", icon: PublicIcon },
];

// Visitor card colors (matches theme)
const getVisitorCardColor = (key, opacity = 1) => {
  const colors = {
    today: `rgba(181, 29, 110, ${opacity})`,       // Ishqnama Pink
    thisWeek: `rgba(0, 86, 210, ${opacity})`,     // Royal Blue
    thisMonth: `rgba(30, 126, 52, ${opacity})`,   // Forest Green
    thisYear: `rgba(183, 110, 0, ${opacity})`,    // Deep Amber
    overall: `rgba(97, 97, 97, ${opacity})`,      // Slate
  };
  return colors[key] || `rgba(181, 29, 110, ${opacity})`;
};

const AnalyticsTraffic = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/analytics.php");
        if (cancelled) return;
        if (res?.data?.success && res?.data?.data) setApiData(res.data.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visitors = apiData?.visitors ?? MOCK_VISITORS;
  const topPages = apiData?.topPages?.length ? apiData.topPages : MOCK_TOP_PAGES;
  const locations = apiData?.locations?.length ? apiData.locations : MOCK_LOCATIONS;
  const devices = apiData?.devices?.length ? apiData.devices : MOCK_DEVICES;
  const dailyVisits = apiData?.dailyVisits?.length ? apiData.dailyVisits : MOCK_DAILY_VISITS;
  const visitsByTimeRange = apiData?.visitsByTimeRange?.length ? apiData.visitsByTimeRange : MOCK_VISITS_BY_TIME_RANGE;
  // Last 24h rolling: API returns buckets from 24h ago to current hour, labels in IST (e.g. "6pm yesterday" … "5pm today")
  const visitsByHour = apiData?.visitsByHour?.length ? apiData.visitsByHour : MOCK_VISITS_BY_HOUR;
  const recentActivity = apiData?.recentActivity?.length ? apiData.recentActivity : MOCK_RECENT_ACTIVITIES;

  const isDemoData = !apiData || error;

  return (
    <>
      <Helmet>
        <title>Analytics & Traffic - Ishqnama Art</title>
        <meta name="description" content="View site analytics, visitor counts, top pages, and traffic by location." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 1.5, sm: 2.5, md: 3 },
          px: { xs: 1, sm: 2 },
          minHeight: "100%",
          background: '#FAF9F6',
        }}
      >


        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Visitor count cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)",
            },
            gap: { xs: 2, md: 3 },
            mb: { xs: 4, md: 8 },
          }}
        >
          {visitorCards.map(({ key, label, icon: Icon }) => {
            const color = getVisitorCardColor(key);
            return (
              <Card
                key={key}
                elevation={0}
                sx={{
                  borderRadius: "24px",
                  background: "#FFFFFF",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.02)",
                  transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                  "&:hover": {
                    boxShadow: `0 20px 40px rgba(0, 0, 0, 0.06)`,
                    transform: "translateY(-6px)",
                    borderColor: getVisitorCardColor(key, 0.15),
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "16px",
                      background: getVisitorCardColor(key, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: color,
                      mb: 3,
                    }}
                  >
                    <Icon sx={{ fontSize: 24 }} />
                  </Box>
                  {loading ? (
                    <Skeleton variant="text" width="60%" height={40} />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: "1.5rem", md: "2rem" }, color: "text.primary", lineHeight: 1, mb: 1, letterSpacing: '-0.03em' }}>
                      {visitors[key].toLocaleString()}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ fontWeight: 800, fontSize: "0.7rem", color: "text.secondary", textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    {label}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Devices & weekly trend - graphs on top */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1.5fr" },
            gap: { xs: 2, md: 3 },
            mb: { xs: 2, md: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              p: 4,
              background: "#FFFFFF",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
              <Box sx={{ p: 1, borderRadius: '10px', background: 'rgba(181, 29, 110, 0.05)', color: 'primary.main', display: 'flex' }}>
                <DeviceHubIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.15rem", fontFamily: "'Playfair Display', serif" }}>
                Traffic Sources
              </Typography>
            </Box>
            <Box sx={{ width: "100%", height: isMobile ? 260 : 300, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <PieChart
                series={[
                  {
                    data: devices.map((d, i) => ({
                      id: i,
                      value: d.visits,
                      label: `${d.device} (${d.visits.toLocaleString()} visits)`,
                      color: d.device === "Mobile" ? "#B51D6E" : d.device === "Desktop" ? "#3B82F6" : "#F59E0B",
                    })),
                    innerRadius: isMobile ? 40 : 60,
                    outerRadius: isMobile ? 100 : 120,
                    paddingAngle: 4,
                    cornerRadius: 8,
                  },
                ]}
                width={undefined}
                height={undefined}
                sx={{
                  width: "100%",
                  height: "100%",
                  "& .MuiChartsLegend-series p": { fontSize: "0.85rem", fontWeight: 700 },
                }}
              />
            </Box>
            <Stack direction="row" justifyContent="center" spacing={3} sx={{ mt: 2 }}>
              {devices.map((d) => (
                <Box key={d.device} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    {d.device}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900, color: 'text.primary' }}>
                    {d.visits.toLocaleString()} visits
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              p: 4,
              background: "#FFFFFF",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
              <Box sx={{ p: 1, borderRadius: '10px', background: 'rgba(59, 130, 246, 0.05)', color: '#0056D2', display: 'flex' }}>
                <TrendingUpIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.15rem", fontFamily: "'Playfair Display', serif" }}>
                Weekly Engagement
              </Typography>
            </Box>
            <Box sx={{ width: "100%", height: isMobile ? 240 : 320 }}>
              <BarChart
                dataset={dailyVisits}
                xAxis={[{
                  scaleType: "band",
                  dataKey: "day",
                  tickLabelStyle: { fontSize: 11, fontWeight: 700, fill: '#6B7280' }
                }]}
                series={[{
                  dataKey: "visits",
                  label: "Store Visits",
                  color: "#B51D6E"
                }]}
                height={isMobile ? 240 : 320}
                margin={{ top: 10, right: 10, bottom: 20, left: 40 }}
                sx={{
                  "& .MuiChartsAxis-bottom .MuiChartsAxis-line": { stroke: 'rgba(0,0,0,0.1)' },
                  "& .MuiChartsAxis-left .MuiChartsAxis-line": { stroke: 'none' },
                  "& .MuiChartsAxis-tick": { stroke: 'rgba(0,0,0,0.1)' },
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* Visit time analytics & Last 24 Hours Activity Log - Combined in One Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1.2fr" },
            gap: { xs: 2, md: 3 },
            mb: { xs: 3, md: 4 },
          }}
        >
          {/* Visit Time Analytics Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              p: 4,
              background: "#FFFFFF",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Box sx={{ p: 1, borderRadius: '8px', background: 'rgba(181, 29, 110, 0.05)', color: 'primary.main', display: 'flex' }}>
                  <ScheduleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.15rem", fontFamily: "'Playfair Display', serif" }}>
                  Visit Time Analytics
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 4, fontWeight: 700, opacity: 0.6 }}>
                Active Periods (Local visit intensity IST)
              </Typography>
              <Stack spacing={3}>
                {visitsByTimeRange.map((tr) => (
                  <Box key={tr.label}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.03)'
                        }}>
                          {tr.icon === "night" && <NightlightIcon sx={{ color: "#4B5563", fontSize: 18 }} />}
                          {tr.icon === "morning" && <WbSunnyIcon sx={{ color: "#F59E0B", fontSize: 18 }} />}
                          {tr.icon === "afternoon" && <WbSunnyIcon sx={{ color: "#EF4444", fontSize: 18 }} />}
                          {tr.icon === "evening" && <ScheduleIcon sx={{ color: "#818CF8", fontSize: 18 }} />}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{tr.label}</Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: '0.75rem' }}>{tr.range}</Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 900, fontSize: "0.9rem", color: 'primary.main' }}>{tr.percent}%</Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: "rgba(0,0,0,0.03)",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${tr.percent}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        style={{
                          height: "100%",
                          borderRadius: 5,
                          background: tr.icon === "morning" ? "#F59E0B" : tr.icon === "afternoon" ? "#EF4444" : tr.icon === "evening" ? "#818CF8" : "#4B5563",
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
            <Box sx={{ mt: 4, p: 3, borderRadius: '16px', background: 'rgba(181, 29, 110, 0.04)', border: '1px solid rgba(181, 29, 110, 0.05)' }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Chip size="small" label="Peak Moment" sx={{ background: 'primary.main', color: 'white', fontWeight: 900, fontSize: '0.65rem' }} />
              </Stack>
              <Typography variant="body1" sx={{ fontWeight: 800, color: "text.primary", fontSize: '1rem', lineHeight: 1.4 }}>
                {visitsByTimeRange.length ? (() => {
                  const peak = visitsByTimeRange.reduce((a, b) => (b.percent > a.percent ? b : a), visitsByTimeRange[0]);
                  return `Engagement peaks during ${peak.label} hours (${peak.percent}%)`;
                })() : "Activity peaks at 42% in the morning."}
              </Typography>
            </Box>
          </Paper>

          {/* Last 24 Hours Activity Log Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              overflow: "hidden",
              background: "#FFFFFF",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 4,
                py: 3,
                borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: '8px', background: 'rgba(181, 29, 110, 0.05)', color: 'primary.main', display: 'flex' }}>
                  <HistoryIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.15rem", fontFamily: "'Playfair Display', serif" }}>
                    Last 24 Hours Activity
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                    Real-time visits with location & timestamp
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${recentActivity.length} Logged`}
                size="small"
                sx={{ fontWeight: 800, bgcolor: 'rgba(181, 29, 110, 0.08)', color: 'primary.main' }}
              />
            </Box>
            <TableContainer sx={{ flex: 1, maxHeight: 440 }}>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap", fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2, px: 3 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>Page Visited</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActivity.map((act, idx) => (
                    <TableRow key={idx} sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.015)" }, transition: 'background 0.2s' }}>
                      <TableCell sx={{ py: 2, px: 3, minWidth: 150, whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.8rem' }}>
                          {dayjs.utc(act.visited_at).tz("Asia/Kolkata").format('DD MMM, hh:mm A')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem', display: 'block' }}>
                          {dayjs.utc(act.visited_at).tz("Asia/Kolkata").fromNow()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>
                        <Chip
                          label={act.device || 'Mobile'}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.65rem',
                            bgcolor: act.device === 'Desktop' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(181, 29, 110, 0.1)',
                            color: act.device === 'Desktop' ? '#0056D2' : 'primary.main',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>
                        <Link
                          href={`https://www.ishqnamaart.in${act.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {act.path}
                        </Link>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, px: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                          <Typography sx={{ fontSize: "1.1rem" }}>{act.flag || '🌐'}</Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: 'text.primary' }}>{act.city || 'Unknown'}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}>{act.country || 'Unknown'}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Most visited pages & Top locations */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* Most visited pages */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              overflow: "hidden",
              background: "#FFFFFF",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
            }}
          >
            <Box
              sx={{
                px: 4,
                py: 3,
                borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ p: 1, borderRadius: '8px', background: 'rgba(181, 29, 110, 0.05)', color: 'primary.main', display: 'flex' }}>
                <LanguageIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.1rem", fontFamily: "'Playfair Display', serif" }}>
                Top Performing Links
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>URL Path</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>Arrivals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topPages.map((row, idx) => (
                    <TableRow key={idx} sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.015)" }, transition: 'background 0.2s' }}>
                      <TableCell sx={{ py: 2.5, px: 4 }}>
                        <Link
                          href={`https://www.ishqnamaart.in${row.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row.path}
                        </Link>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: 'text.primary', fontSize: '0.85rem', px: 4 }}>{row.visits.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Top locations */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              overflow: "hidden",
              background: "#FFFFFF",
              boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
            }}
          >
            <Box
              sx={{
                px: 4,
                py: 3,
                borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ p: 1, borderRadius: '8px', background: 'rgba(59, 130, 246, 0.05)', color: '#0056D2', display: 'flex' }}>
                <PlaceIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", fontSize: "1.1rem", fontFamily: "'Playfair Display', serif" }}>
                Global Footprint
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>Location</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', bgcolor: '#F9FAFB', py: 2 }}>Arrivals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locations.map((row, idx) => (
                    <TableRow key={idx} sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.015)" }, transition: 'background 0.2s' }}>
                      <TableCell sx={{ py: 2, px: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                          <Typography sx={{ fontSize: "1.6rem" }}>{row.flag}</Typography>
                          <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: "0.85rem", color: 'text.primary' }}>{row.city}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>{row.country}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: 'text.primary', fontSize: '0.85rem', px: 4 }}>{row.visits.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>



        {/* Info chip */}
        <Box sx={{ mt: { xs: 2, md: 3 }, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Chip
            label={isDemoData ? "Demo data" : "Live data"}
            size="small"
            sx={{
              bgcolor: isDemoData ? "rgba(139, 53, 85, 0.1)" : "rgba(76, 175, 80, 0.15)",
              color: isDemoData ? "#8B3555" : "#2e7d32",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}>
            {isDemoData
              ? "Connect your analytics API to show real data (e.g. Google Analytics, Plausible, or custom backend)."
              : "Data from your stored analytics events."}
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default AnalyticsTraffic;
